import React, { useState, useEffect } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Copy, ExternalLink, Settings, Plus, Trash2, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { WidgetCustomizer } from './WidgetCustomizer';
import { WidgetPreview } from './WidgetPreview';
import { DomainManager } from './DomainManager';
import { EmbedCodeGenerator } from './EmbedCodeGenerator';

interface WidgetToken {
  id: string;
  token: string;
  domain_whitelist: string[] | null;
  is_active: boolean;
  created_at: string;
  widget_id?: string;
}

interface WidgetConfig {
  id: string;
  organization_id: string;
  name: string;
  domain_whitelist: string[] | null;
  custom_css: Record<string, any> | null;
  branding_config: Record<string, any> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const Hive360SettingsPanel: React.FC = () => {
  const { organization, loading } = useOrganization();
  const [tokens, setTokens] = useState<WidgetToken[]>([]);
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [selectedWidget, setSelectedWidget] = useState<WidgetConfig | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [activeTab, setActiveTab] = useState<'tokens' | 'customization' | 'preview'>('tokens');

  // Load existing tokens and widgets
  useEffect(() => {
    if (!loading && organization) {
      loadTokensAndWidgets();
    }
  }, [organization, loading]);

  const loadTokensAndWidgets = async () => {
    if (!organization) return;
    
    try {
      // Load tokens
      const { data: tokensData, error: tokensError } = await supabase
        .from('hive360_tokens')
        .select('*')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (tokensError) throw tokensError;

      // Load widgets (this might fail if migration hasn't run yet)
      const { data: widgetsData, error: widgetsError } = await supabase
        .from('hive360_widgets')
        .select('*')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (widgetsError) {
        console.warn('hive360_widgets table not found, migration may not have run yet');
        setWidgets([]);
      } else {
        setWidgets(widgetsData || []);
        if (widgetsData && widgetsData.length > 0) {
          setSelectedWidget(widgetsData[0]);
        }
      }

      setTokens(tokensData || []);
    } catch (error) {
      console.error('Error loading tokens and widgets:', error);
      toast.error('Failed to load Hive360 configuration');
    } finally {
      setLoadingTokens(false);
    }
  };

  const generateToken = async () => {
    if (!organization) return;
    
    setIsGeneratingToken(true);
    try {
      // Generate a simple token (in production, this would be more secure)
      const token = `hive360_${organization.id.substring(0, 8)}_${Date.now()}`;
      
      const { data, error } = await supabase
        .from('hive360_tokens')
        .insert({
          organization_id: organization.id,
          token,
          domain_whitelist: newDomain ? [newDomain] : null,
        })
        .select()
        .single();

      if (error) throw error;
      
      setTokens([data, ...tokens]);
      setNewDomain('');
      toast.success('Widget token generated successfully');
    } catch (error) {
      console.error('Error generating token:', error);
      toast.error('Failed to generate widget token');
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const deleteToken = async (tokenId: string) => {
    try {
      const { error } = await supabase
        .from('hive360_tokens')
        .update({ is_active: false })
        .eq('id', tokenId);

      if (error) throw error;
      
      setTokens(tokens.filter(t => t.id !== tokenId));
      toast.success('Widget token deleted');
    } catch (error) {
      console.error('Error deleting token:', error);
      toast.error('Failed to delete widget token');
    }
  };

  if (loading) {
    return <div>Loading Hive360 Settings...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start gap-4 flex-1">
        <div className="p-4 rounded-full flex items-center justify-center bg-primary/10 bg-gradient-to-br from-primary/20 to-primary/10 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
          <Settings className="h-6 w-6 relative z-10 transition-transform duration-300 group-hover:scale-110 text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Hive360 Widget Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your embeddable class enrollment widgets
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('tokens')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tokens'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tokens & Security
          </button>
          <button
            onClick={() => setActiveTab('customization')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'customization'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Palette className="h-4 w-4 inline mr-2" />
            Customization
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'preview'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Preview & Testing
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'tokens' && (
        <div className="space-y-6">
          {/* Generate New Token */}
          <Card>
            <CardHeader>
              <CardTitle>Generate Widget Token</CardTitle>
              <CardDescription>
                Create a new token to embed the class enrollment widget on your website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="domain">Allowed Domain (Optional)</Label>
                <Input
                  id="domain"
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Leave empty to allow embedding on any domain
                </p>
              </div>
              <Button 
                onClick={generateToken} 
                disabled={isGeneratingToken}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {isGeneratingToken ? 'Generating...' : 'Generate Token'}
              </Button>
            </CardContent>
          </Card>

          {/* Existing Tokens */}
          <Card>
            <CardHeader>
              <CardTitle>Active Widget Tokens</CardTitle>
              <CardDescription>
                Manage your existing widget tokens and embed codes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingTokens ? (
                <div>Loading tokens...</div>
              ) : tokens.length === 0 ? (
                <p className="text-muted-foreground">No widget tokens generated yet</p>
              ) : (
                <div className="space-y-4">
                  {tokens.map((token) => (
                    <div key={token.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {token.domain_whitelist?.length 
                                ? `${token.domain_whitelist.join(', ')}`
                                : 'Any domain'
                              }
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground font-mono">
                            {token.token}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <EmbedCodeGenerator token={token.token} organizationId={organization?.id} />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteToken(token.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Domain Management */}
          <DomainManager tokens={tokens} onTokensUpdate={setTokens} />
        </div>
      )}

      {activeTab === 'customization' && (
        <WidgetCustomizer 
          widget={selectedWidget} 
          onWidgetUpdate={setSelectedWidget}
          organizationId={organization?.id}
        />
      )}

      {activeTab === 'preview' && (
        <div className="space-y-6">
          <WidgetPreview 
            widget={selectedWidget} 
            tokens={tokens}
            organizationId={organization?.id}
          />
          
          {/* Testing Links */}
          <Card>
            <CardHeader>
              <CardTitle>Testing & Integration</CardTitle>
              <CardDescription>
                Test your widget integration and access resources
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {tokens.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Live Widget Testing</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Test your widget with real class data and functional enrollment:
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open('/admin/widget-test', '_blank')}
                      className="w-full"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Live Test Environment
                    </Button>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-2">Integration Ready</h4>
                    <p className="text-sm text-green-700">
                      Your widget is ready to embed! Copy the embed code above and paste it into your website.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                  <p className="text-muted-foreground">
                    Generate your first token to start testing the widget
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};